/**
 * NavigationGuardContext - 미저장 변경사항 감지 및 네비게이션 차단
 *
 * 사용 사례:
 * - Settings 페이지에서 저장하지 않고 다른 페이지로 이동하려 할 때 경고
 * - Form 작성 중 실수로 이동하는 것 방지
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface NavigationGuardContextType {
  shouldBlockNavigation: boolean;
  setShouldBlockNavigation: (block: boolean) => void;
  confirmNavigation: (() => void) | null;
  cancelNavigation: (() => void) | null;
  showNavigationDialog: (onConfirm: () => void, onCancel?: () => void) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextType | undefined>(undefined);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  const [cancelCallback, setCancelCallback] = useState<(() => void) | null>(null);

  const showNavigationDialog = useCallback((onConfirm: () => void, onCancel?: () => void) => {
    setConfirmCallback(() => onConfirm);
    setCancelCallback(() => onCancel || (() => {}));
    setIsDialogOpen(true);
  }, []);

  const handleConfirm = () => {
    setIsDialogOpen(false);
    setShouldBlockNavigation(false);
    if (confirmCallback) {
      confirmCallback();
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    if (cancelCallback) {
      cancelCallback();
    }
  };

  return (
    <NavigationGuardContext.Provider
      value={{
        shouldBlockNavigation,
        setShouldBlockNavigation,
        confirmNavigation: confirmCallback,
        cancelNavigation: cancelCallback,
        showNavigationDialog,
      }}
    >
      {children}

      {/* Navigation Confirmation Dialog */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장하지 않은 변경사항이 있습니다</AlertDialogTitle>
            <AlertDialogDescription>
              페이지를 떠나면 변경사항이 손실됩니다. 계속하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>나가기</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const context = useContext(NavigationGuardContext);
  if (context === undefined) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }
  return context;
}
